import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useUser } from '../context/UserContext';
import {
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
    ExclamationCircleIcon
} from '@heroicons/react/20/solid';

// A map to hold styles and icons for different alert statuses
const statusMap = {
  critical: { Icon: XCircleIcon, style: 'text-red-600', border: 'border-red-300', background: 'bg-red-50' },
  warning: { Icon: ExclamationTriangleIcon, style: 'text-yellow-600', border: 'border-yellow-300', background: 'bg-yellow-50' },
  info: { Icon: InformationCircleIcon, style: 'text-blue-600', border: 'border-blue-300', background: 'bg-blue-50' },
};

export default function AlertsList() {
  const { user, loading: userLoading } = useUser();
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (userLoading || !user) {
      if (!userLoading) {
        setAlertsLoading(false);
      }
      return;
    }

    const fetchAllAlerts = async () => {
      setAlertsLoading(true);
      const allAlertsData = [];
      try {
        // Fetch data for Pending Invites
        const { data: userProfile, error: profileError } = await supabase
          .from('profile')
          .select('email_address')
          .eq('id', user.id)
          .single();
        
        if (profileError && profileError.code !== 'PGRST116') throw profileError;

        if (userProfile?.email_address) {
          const { data: invitesData } = await supabase.from('organization_invites').select('id, organization_id, organizations(name)').eq('email', userProfile.email_address).eq('status', 'pending');
          if (invitesData) {
            invitesData.forEach(invite => {
              allAlertsData.push({
                id: invite.id,
                type: 'invite',
                status: 'warning',
                title: `You're invited to join ${invite.organizations.name}`,
                description: 'Accept the invitation to start collaborating.',
                data: invite,
              });
            });
          }
        }

        // Fetch org membership to check for other alerts
        const { data: orgMember, error: orgMemberError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgMemberError && orgMemberError.code !== 'PGRST116') {
          throw orgMemberError;
        }
        
        // Add "Not in Company" alert if applicable
        if (!orgMember) {
          allAlertsData.push({
            id: 'no-company',
            type: 'linkButton',
            status: 'critical',
            title: 'Complete Your Account Setup',
            description: 'You are not yet a member of a team.',
            href: '/settings/Team',
            actionText: 'Team Page',
            hasArrow: true,
          });
        } else {
          // Add "Unmapped Measurements" alert if applicable
          const { count: unmappedCount } = await supabase.from('measurements_with_details').select('*', { count: 'exact', head: true }).eq('organization_id', orgMember.organization_id).is('project_id', null);
          
          if (unmappedCount && unmappedCount > 0) {
            allAlertsData.push({
              id: 'unmapped-measurements',
              type: 'linkButton',
              status: 'warning',
              title: 'Unmapped Measurements',
              description: `${unmappedCount} measurements need to be assigned to a project.`,
              href: '/unmappedproject',
              actionText: 'Assign to Project',
              hasArrow: false,
            });
          }
        }

        setAlerts(allAlertsData);
      } catch (error) {
        console.error("Error fetching alerts:", error);
      } finally {
        setAlertsLoading(false);
      }
    };

    fetchAllAlerts();
  }, [user, userLoading]);
  
  const handleInviteAction = async (action, invite) => {
    setActionLoading(invite.id);
    try {
      if (action === 'accept') {
        await supabase.from('organization_members').insert([{ user_id: user.id, organization_id: invite.organization_id, role: 'member' }]);
        await supabase.from('organization_invites').update({ status: 'accepted' }).eq('id', invite.id);
      } else { // Decline
        await supabase.from('organization_invites').update({ status: 'declined' }).eq('id', invite.id);
      }
      window.location.reload(); 
    } catch (err) {
      console.error(`Failed to ${action} invite:`, err);
      setActionLoading(null);
    }
  };

  if (userLoading || alertsLoading) return null;
  if (alerts.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-x-4 px-6 py-4 border-b border-gray-200 bg-gray-50">
  <div className="flex-shrink-0 rounded-full bg-white p-2 border border-gray-200 shadow-sm">
    <ExclamationCircleIcon className="h-6 w-6 text-gray-500" aria-hidden="true" />
  </div>
  <div>
    <h2 className="text-base font-semibold text-gray-900">Action Items</h2>
    <p className="text-sm text-gray-600">Notifications and items that require your attention.</p>
  </div>
</div>

      <div className="p-6 space-y-4">
        {alerts.map((alert) => {
            const { Icon, style: iconStyle, border: borderStyle, background: backgroundStyle } = statusMap[alert.status] || statusMap.info;
            const isActionLoading = actionLoading === alert.id;

            return (
              <div key={alert.id} className={`flex items-center gap-4 rounded-lg border p-4 shadow-sm ${backgroundStyle} ${borderStyle}`}>
                {/* Icon */}
                <div className="shrink-0">
                  <Icon className={`h-6 w-6 ${iconStyle}`} aria-hidden="true" />
                </div>

                {/* Title & Description */}
                <div className="flex-auto min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{alert.title}</p>
                  <p className="text-sm text-gray-500">{alert.description}</p>
                </div>

                {/* Actions */}
                <div className="shrink-0 ml-auto flex flex-shrink-0 items-center gap-2">
                  {alert.type === 'invite' && (
                    <>
                      <button
                        onClick={() => handleInviteAction('accept', alert.data)}
                        disabled={isActionLoading}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircleIcon className="h-5 w-5 -ml-1 mr-1.5" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleInviteAction('decline', alert.data)}
                        disabled={isActionLoading}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircleIcon className="h-5 w-5 -ml-1 mr-1.5" />
                        Decline
                      </button>
                    </>
                  )}
                  {alert.type === 'linkButton' && (
                    <a
                      href={alert.href}
                      className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      {alert.actionText}
                      {alert.hasArrow && <ChevronRightIcon className="h-5 w-5 -mr-1 ml-1.5 text-gray-400" />}
                    </a>
                  )}
                </div>
              </div>
            );
        })}
      </div>
    </div>
  );
}